import { useEffect, useState, useRef, useCallback } from "react";
import { Box, Typography, Button, Stack, Paper, IconButton, Fade, Grow } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";

const PULSE_KEYFRAMES = `
@keyframes onboarding-pulse {
  0% { box-shadow: 0 0 0 0 rgba(99,102,241,0.5); }
  70% { box-shadow: 0 0 0 14px rgba(99,102,241,0); }
  100% { box-shadow: 0 0 0 0 rgba(99,102,241,0); }
}
@keyframes onboarding-glow {
  0%, 100% { opacity: 0.7; }
  50% { opacity: 1; }
}
`;

function getTargetRect(targetId) {
  const el = document.querySelector(`[data-onboarding-id="${targetId}"]`);
  if (!el) return null;
  return { el, rect: el.getBoundingClientRect() };
}

const TOOLTIP_H = 285;
const TOOLTIP_W = 320;

function getTooltipPosition(rect, padding = 12) {
  const viewW = window.innerWidth;
  const viewH = window.innerHeight;
  const centerX = rect.left + rect.width / 2;
  const leftClamped = Math.max(16, Math.min(centerX - TOOLTIP_W / 2, viewW - TOOLTIP_W - 16));

  const below = rect.bottom + padding;
  const aboveTop = rect.top - padding - TOOLTIP_H;

  // Prefer below the target
  if (below + TOOLTIP_H < viewH - 8) {
    return { top: below, left: leftClamped };
  }
  // Try above the target
  if (aboveTop > 8) {
    return { top: aboveTop, left: leftClamped };
  }
  // Fallback: pin near bottom of viewport with safe margin
  return { top: Math.max(8, viewH - TOOLTIP_H - 16), left: leftClamped };
}

export default function OnboardingOverlay({ currentStep, stepNumber, totalSteps, onNext, onPrev, onDismiss }) {
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState(null);
  const [visible, setVisible] = useState(false);
  const styleRef = useRef(null);

  // Inject keyframes
  useEffect(() => {
    if (!styleRef.current) {
      const style = document.createElement("style");
      style.textContent = PULSE_KEYFRAMES;
      document.head.appendChild(style);
      styleRef.current = style;
    }
    return () => {
      if (styleRef.current) {
        styleRef.current.remove();
        styleRef.current = null;
      }
    };
  }, []);

  const updatePosition = useCallback(() => {
    if (!currentStep) return;
    const result = getTargetRect(currentStep.targetId);
    if (result) {
      // Scroll the element into view if it's inside a dialog or off-screen
      result.el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      // Wait for scroll to settle, then measure
      setTimeout(() => {
        const fresh = getTargetRect(currentStep.targetId);
        if (fresh) {
          setTargetRect(fresh.rect);
          setTooltipPos(getTooltipPosition(fresh.rect));
        }
      }, 120);
    }
  }, [currentStep]);

  useEffect(() => {
    if (!currentStep) {
      setVisible(false);
      return;
    }
    setVisible(false);
    // Small delay to allow tab changes or dialog opens to render
    const timer = setTimeout(() => {
      updatePosition();
      setVisible(true);
    }, 350);
    return () => clearTimeout(timer);
  }, [currentStep, updatePosition]);

  useEffect(() => {
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [updatePosition]);

  // Add pulse class to target
  useEffect(() => {
    if (!currentStep) return;
    const result = getTargetRect(currentStep.targetId);
    if (!result) return;
    const el = result.el;
    el.style.animation = "onboarding-pulse 1.8s infinite";
    el.style.position = "relative";
    el.style.zIndex = "1401";
    el.style.borderRadius = el.style.borderRadius || "8px";
    return () => {
      el.style.animation = "";
      el.style.zIndex = "";
    };
  }, [currentStep]);

  if (!currentStep || !visible || !targetRect) return null;

  const isLast = stepNumber === totalSteps;
  const isFirst = stepNumber === 1;
  const pad = 8;
  const spotRect = {
    top: targetRect.top - pad,
    left: targetRect.left - pad,
    width: targetRect.width + pad * 2,
    height: targetRect.height + pad * 2,
  };

  return (
    <>
      {/* Backdrop with cutout */}
      <Fade in timeout={300}>
        <Box
          onClick={onDismiss}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1400,
            background: `radial-gradient(
              ellipse ${spotRect.width + 30}px ${spotRect.height + 30}px
              at ${spotRect.left + spotRect.width / 2}px ${spotRect.top + spotRect.height / 2}px,
              transparent 60%,
              rgba(0,0,0,0.55) 100%
            )`,
            pointerEvents: "auto",
            cursor: "default",
          }}
        />
      </Fade>

      {/* Spotlight ring */}
      <Fade in timeout={400}>
        <Box
          sx={{
            position: "fixed",
            top: spotRect.top,
            left: spotRect.left,
            width: spotRect.width,
            height: spotRect.height,
            borderRadius: 2,
            border: "2px solid rgba(99,102,241,0.6)",
            zIndex: 1401,
            pointerEvents: "none",
            animation: "onboarding-glow 2s ease-in-out infinite",
          }}
        />
      </Fade>

      {/* Tooltip */}
      <Grow in timeout={400}>
        <Paper
          elevation={12}
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: "fixed",
            top: tooltipPos?.top || 0,
            left: tooltipPos?.left || 0,
            zIndex: 1402,
            width: TOOLTIP_W,
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
            borderRadius: 3,
            overflow: "hidden",
            pointerEvents: "auto",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              bgcolor: "navy.main",
              color: "#fff",
              px: 2.5,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <RocketLaunchIcon sx={{ fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight={700}>
                Paso {stepNumber} de {totalSteps}
              </Typography>
            </Stack>
            <IconButton size="small" onClick={onDismiss} aria-label="Cerrar tour" sx={{ color: "#fff", opacity: 0.8, "&:hover": { opacity: 1 } }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* Progress bar */}
          <Box sx={{ height: 3, bgcolor: "divider" }}>
            <Box
              sx={{
                height: "100%",
                width: `${(stepNumber / totalSteps) * 100}%`,
                bgcolor: "primary.main",
                transition: "width 0.4s ease",
              }}
            />
          </Box>

          {/* Body */}
          <Box sx={{ px: 2.5, py: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              {currentStep.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              {currentStep.description}
            </Typography>
          </Box>

          {/* Actions */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, pb: 1.5 }}>
            <Button size="small" onClick={onDismiss} sx={{ color: "text.secondary", textTransform: "none" }}>
              Omitir tour
            </Button>
            <Stack direction="row" spacing={1}>
              {!isFirst && (
                <Button size="small" startIcon={<ArrowBackIcon />} onClick={onPrev} sx={{ textTransform: "none" }}>
                  Atrás
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                endIcon={isLast ? <CheckCircleIcon /> : <ArrowForwardIcon />}
                onClick={onNext}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                {isLast ? "Terminar" : "Siguiente"}
              </Button>
            </Stack>
          </Stack>

          {/* Step dots */}
          <Stack direction="row" justifyContent="center" spacing={0.5} pb={1.5}>
            {Array.from({ length: totalSteps }, (_, i) => (
              <Box
                key={i}
                sx={{
                  width: i + 1 === stepNumber ? 18 : 6,
                  height: 6,
                  borderRadius: 3,
                  bgcolor: i + 1 === stepNumber ? "primary.main" : i + 1 < stepNumber ? "secondary.light" : "divider",
                  transition: "all 0.3s ease",
                }}
              />
            ))}
          </Stack>
        </Paper>
      </Grow>
    </>
  );
}
